import React, { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchUserById } from '../../../services/api/endpoints/users';
import { FabrikUser } from '../../../types';
import UserDetailContent from './UserDetailContent';

interface UserDetailModalProps {
  open: boolean;
  onClose: () => void;
  userId: string | null;
  onCheckInClick?: (userId: string) => void;
}

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  open,
  onClose,
  userId,
  onCheckInClick,
}) => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<FabrikUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && userId && user?.token) {
      setLoading(true);
      setError(null);
      setUserData(null);

      fetchUserById(user.token, userId, true)
        .then((data) => {
          setUserData(data);
        })
        .catch((err) => {
          console.error('Error fetching user details:', err);
          setError(
            err.response?.status === 404
              ? 'User not found'
              : 'Failed to load user details. Please try again.'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [open, userId, user?.token]);

  const handleClose = () => {
    setUserData(null);
    setError(null);
    onClose();
  };

  const fetchUserData = () => {
    if (userId && user?.token) {
      setLoading(true);
      setError(null);
      setUserData(null);

      fetchUserById(user.token, userId, true)
        .then((data) => {
          setUserData(data);
        })
        .catch((err) => {
          console.error('Error fetching user details:', err);
          setError(
            err.response?.status === 404
              ? 'User not found'
              : 'Failed to load user details. Please try again.'
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="user-detail-modal-title"
      aria-describedby="user-detail-modal-description"
      BackdropProps={{
        timeout: 200, // Faster backdrop transition
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)', // Consistent backdrop color
        }
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
          maxWidth: '1200px',
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'grey.50',
          }}
        >
          <Typography
            id="user-detail-modal-title"
            variant="h6"
            component="h2"
            sx={{ fontWeight: 600 }}
          >
            User Details
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 3,
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '200px',
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {userData && !loading && !error && (
            <UserDetailContent 
              user={userData} 
              onCheckInClick={() => {
                if (onCheckInClick && userId) {
                  onClose(); // Close the UserDetailModal
                  onCheckInClick(userId); // Signal parent to open CheckinModal
                }
              }}
              onUserDataRefresh={() => {
                // Refresh user data after successful check-in
                if (userId) {
                  fetchUserData();
                }
              }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default UserDetailModal;
